import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Archive } from "lucide-react";
import { Ingredient } from "../types/Types";
import OSCard, { OS_EMPTY_CLS } from "./OSCard";

/**
 * Archive metadata card on /os/archive — right column, directly above Податоци, same table style.
 *
 * The archive view deliberately has NO picker list: it shows the single most-recently
 * archived normativ, and the full normativ layout underneath renders it read-only. This card
 * carries the handful of fields that only exist on an archived record (archived-by, reason)
 * plus the version/validity it was archived at.
 *
 * Purely presentational — extracted out of screens/OS.tsx, which is far over the screen-file
 * size limit. Renders nothing outside archive view.
 */

interface Props {
  /** False on the live /os screen — the component then renders nothing at all. */
  isArchiveView: boolean;
  loadingArchive: boolean;
  /** Only its emptiness matters here: no rows means nothing has been archived yet. */
  hasArchivedRecords: boolean;
  loadingArchiveDetail: boolean;
  archiveDetail: Ingredient | null;
  /** Resolves an archivedBy/updatedBy ref (id string or populated object) to a display name. */
  formatArchiveUser: (user: Ingredient["archivedBy"] | Ingredient["updatedBy"]) => string;
}

const EMPTY = "—";

/** Same pill shape as OSNormativStatusBadge; colours follow the Идент status meaning. */
const STATUS_PILL_CLS: Record<string, string> = {
  active: "bg-green-50 text-green-700 ring-green-200",
  inactive: "bg-gray-100 text-gray-600 ring-gray-300",
  export: "bg-blue-50 text-blue-700 ring-blue-200",
  development: "bg-blue-50 text-blue-700 ring-blue-200",
  pendingCancellation: "bg-amber-50 text-amber-700 ring-amber-200",
};

/** Dates are stored either as DD.MM.YYYY strings (validTo) or ISO (archivedAt). */
const toDisplayDate = (value?: string | null): string => {
  if (!value) return EMPTY;
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : format(date, "dd.MM.yyyy");
};

// Same label/value table as the Податоци card (DetailDataCard) — this card sits right above it.
const TH_CLS = "border border-gray-300 bg-gray-200 text-gray-700 font-semibold text-left text-[11px] h-[26px] px-2 w-28";
const TD_CLS = "border border-gray-300 h-[26px] px-2 py-0.5 text-[11px] text-gray-800 tabular-nums";

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <tr>
    <th className={TH_CLS}>{label}</th>
    <td className={TD_CLS}>{children}</td>
  </tr>
);

const OSArchiveInfoBox: React.FC<Props> = ({
  isArchiveView,
  loadingArchive,
  hasArchivedRecords,
  loadingArchiveDetail,
  archiveDetail,
  formatArchiveUser,
}) => {
  const { t } = useTranslation();

  if (!isArchiveView) return null;

  const renderBody = () => {
    if (loadingArchive || loadingArchiveDetail) {
      return <div className={OS_EMPTY_CLS}>{t("common.loading") || "Loading..."}</div>;
    }
    if (!hasArchivedRecords) {
      return <div className={OS_EMPTY_CLS}>{t("os.archiveInfo.noArchive")}</div>;
    }
    if (!archiveDetail) {
      return <div className={OS_EMPTY_CLS}>{t("common.noData") || "No data"}</div>;
    }

    const statuses = Array.isArray(archiveDetail.status) ? archiveDetail.status : [];
    const version = archiveDetail.versionDisplay || String(archiveDetail.version || 1).padStart(3, "0");
    const archivedBy = formatArchiveUser(archiveDetail.archivedBy || archiveDetail.updatedBy);
    const reason = archiveDetail.archivedReason?.trim();

    return (
      <table className="w-full border-collapse table-fixed">
        <tbody>
          <InfoRow label={t("tableHeaders.version")}>{version}</InfoRow>
          <InfoRow label={t("tableHeaders.validTo")}>{toDisplayDate(archiveDetail.validTo)}</InfoRow>
          <InfoRow label={t("tableHeaders.status")}>
            {statuses.length === 0 ? EMPTY : (
              <span className="inline-flex flex-wrap gap-1">
                {statuses.map((status) => (
                  <span
                    key={status}
                    className={`inline-flex items-center rounded-full px-2 py-0 text-[11px] font-semibold ring-1 whitespace-nowrap ${
                      STATUS_PILL_CLS[status] || STATUS_PILL_CLS.inactive
                    }`}
                  >
                    {t(`status.${status}`, status)}
                  </span>
                ))}
              </span>
            )}
          </InfoRow>
          <InfoRow label={t("os.archiveInfo.archivedBy")}>
            {archivedBy && archivedBy !== "-" ? archivedBy : EMPTY}
          </InfoRow>
          <InfoRow label={t("os.archiveInfo.archivedAt")}>{toDisplayDate(archiveDetail.archivedAt)}</InfoRow>
          <InfoRow label={t("os.archiveInfo.changeRecord")}>
            <span className={`block whitespace-pre-wrap py-0.5 ${reason ? "text-gray-800" : "text-gray-400"}`}>
              {reason || EMPTY}
            </span>
          </InfoRow>
        </tbody>
      </table>
    );
  };

  return (
    <div>
      <OSCard
        title={
          <span className="inline-flex items-center gap-2">
            <Archive size={16} />
            {t("os.archiveInfo.title")}
          </span>
        }
      >
        {renderBody()}
      </OSCard>
    </div>
  );
};

export default OSArchiveInfoBox;
